import { useState, useEffect } from 'react';
import { useEntityConfig } from './useEntityConfig';

/**
 * Hook to get options for select/dropdown fields
 * Works with both inline options and dataSource-based options
 * 
 * @param {string} entityName - Entity name (e.g., 'customer')
 * @param {string} fieldName - Field name (e.g., 'Industry')
 * @returns {object} { options: [], loading: boolean, error: string }
 */
export function useFieldOptions(entityName, fieldName) {
  const { config: entityConfig, loading: configLoading } = useEntityConfig(entityName);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (configLoading || !entityConfig || !fieldName) {
      setLoading(configLoading);
      return;
    }

    const loadOptions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get field configuration
        const fieldConfig = entityConfig.fields?.[fieldName];
        
        if (!fieldConfig) {
          setError(`Field ${fieldName} not found in entity config`);
          setOptions([]);
          setLoading(false);
          return;
        }

        // Check if field is a select type
        if (fieldConfig.ui?.inputType !== 'select') {
          setOptions([]);
          setLoading(false);
          return;
        }

        // Case 1: Options are defined inline in the config
        if (fieldConfig.ui?.options && Array.isArray(fieldConfig.ui.options)) {
          setOptions(fieldConfig.ui.options);
          setLoading(false);
          return;
        }

        // Case 2: Options come from dataSource (tenant-specific list values)
        if (fieldConfig.ui?.dataSource === 'listValues' && fieldConfig.ui?.listName) {
          // The entityConfigService already merged list values into the field config
          // Check if options were merged
          if (fieldConfig.ui?.options && Array.isArray(fieldConfig.ui.options)) {
            setOptions(fieldConfig.ui.options);
          } else {
            console.warn(`⚠️ useFieldOptions: No options found for ${fieldName} (listName: ${fieldConfig.ui.listName})`);
            setOptions([]);
          }
          setLoading(false);
          return;
        }

        // Case 3: Validation enum (for backward compatibility)
        if (fieldConfig.validation?.enum && Array.isArray(fieldConfig.validation.enum)) {
          const enumOptions = fieldConfig.validation.enum.map(value => ({
            value,
            label: value
          }));
          console.log(`✅ useFieldOptions: Loaded enum options for ${fieldName}:`, enumOptions);
          setOptions(enumOptions);
          setLoading(false);
          return;
        }

        // No options found
        console.warn(`⚠️ useFieldOptions: No options configuration found for ${fieldName}`);
        setOptions([]);
        setLoading(false);

      } catch (err) {
        console.error(`❌ useFieldOptions: Error loading options for ${fieldName}:`, err);
        setError(err.message);
        setOptions([]);
        setLoading(false);
      }
    };

    loadOptions();
  }, [entityConfig, configLoading, entityName, fieldName]);

  return {
    options,
    loading,
    error,
    isSelectField: options.length > 0 || loading
  };
}

/**
 * Get field input type from entity config
 * 
 * @param {object} entityConfig - Entity configuration
 * @param {string} fieldName - Field name
 * @returns {string} Input type (e.g., 'text', 'select', 'email')
 */
export function getFieldInputType(entityConfig, fieldName) {
  const fieldConfig = entityConfig?.fields?.[fieldName];
  return fieldConfig?.ui?.inputType || 'text';
}

/**
 * Check if a field is a select field
 * 
 * @param {object} entityConfig - Entity configuration
 * @param {string} fieldName - Field name
 * @returns {boolean} True if field is a select field
 */
export function isSelectField(entityConfig, fieldName) {
  const inputType = getFieldInputType(entityConfig, fieldName);
  return inputType === 'select' || inputType === 'dropdown';
}

/**
 * Get options for a field synchronously (if already loaded in config)
 * 
 * @param {object} entityConfig - Entity configuration
 * @param {string} fieldName - Field name
 * @returns {array} Array of options or empty array
 */
export function getFieldOptionsSync(entityConfig, fieldName) {
  if (!entityConfig || !fieldName) return [];
  
  const fieldConfig = entityConfig.fields?.[fieldName];
  
  if (!fieldConfig || fieldConfig.ui?.inputType !== 'select') {
    return [];
  }

  // Return options from config (already merged by entityConfigService)
  if (fieldConfig.ui?.options && Array.isArray(fieldConfig.ui.options)) {
    return fieldConfig.ui.options;
  }

  // Fallback to enum
  if (fieldConfig.validation?.enum && Array.isArray(fieldConfig.validation.enum)) {
    return fieldConfig.validation.enum.map(value => ({
      value,
      label: value
    }));
  }

  return [];
}

